import { tradutorDe, type Tradutor } from '@vestigo/core/i18n-da-tela';
import {
  type EstadoDoEvento,
  entradaAbertaEm,
  entradaEstaAberta,
  estadoDoEvento,
  faltaQuanto,
} from '@vestigo/core/area-agenda';
/*
 * De `chave-de-dia.js`, e NÃO de `grade-do-mes.js` — ver o cabeçalho de lá.
 *
 * Este ficheiro vive no pedaço de ENTRADA (a faixa do próximo encontro entra
 * pelo `import` estático de `PortasDaArea` em `App.tsx`), e importar duas
 * funções de `grade-do-mes.ts` arrastava as outras trinta — 2.351 B de nomes de
 * mês, dias da semana e grade de seis linhas — para a porta da área.
 */
import { UM_DIA, type ChaveDeDia, diaDoInstante, somarDias, utcDoDia } from './chave-de-dia.js';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * ── A CHAMADA DO TEMPO, E ELA É QUEM DECIDE O TAMANHO DO CARTÃO ─────────────
 * ════════════════════════════════════════════════════════════════════════════
 *
 * **A ordem do dono, 25/09/2026**, olhando a coluna da direita da agenda:
 *
 * > *«as abas de proximos ficou confuso, parece que o evento é hoje e nao
 * > reforçou que nao tem nada no dia de hoje, o evento ta chamando muita
 * > atencao apesar de ser no futuro.»*
 *
 * ## O defeito tinha uma causa, e ela era de ESCALA, não de cor
 *
 * O cartão desenhava `19:00` em **23 px mono a 600** e a data em **12 px
 * suave**, ao lado. Quem varre a tela lê o maior algarismo primeiro e conclui
 * *«é hoje às sete»* — e o selo `HOJE`, a 150 px de distância, no cabeçalho do
 * dia, não desfaz a leitura porque chega depois dela. *O cartão estava ordenado
 * por TAMANHO DE CAIXA, e não por distância no tempo.*
 *
 * ## A lei nova: **a unidade da decisão é a unidade do título**
 *
 * | quando é | o que a pessoa decide | o que o cartão grita |
 * |---|---|---|
 * | daqui a 5 dias | se cabe na semana dela | **quantos dias** — `FALTAM 5 DIAS`, o `5` em corpo grande |
 * | amanhã | idem, com nome próprio | `É AMANHÃ` |
 * | hoje | se dá para estar em frente ao ecrã **àquela hora** | **a hora** — `Hoje às 19:00` |
 * | a decorrer | entrar, agora | `Acontecendo agora` |
 *
 * A hora só manda quando o dia já está resolvido. Enquanto o dia é a pergunta, a
 * hora é ruído do tamanho de um título.
 *
 * ## O invariante que isto compra, e que a cerca prova
 *
 * > **Um encontro só desenha em `pleno` se ele for hoje, se já estiver a
 * > decorrer, ou se a entrada já tiver aberto.** Nada que esteja a mais de um
 * > dia de distância pode ser a coisa mais gritante da tela.
 *
 * É a mesma frase por dois lados: `pesoDoEncontro` é a ÚNICA fonte do peso — o
 * cartão não recebe mais um `destaque?: boolean` de quem o monta. Antes recebia,
 * e era por isso que a tela podia discordar do relógio sem ninguém dar por ela:
 * quem passava a prop decidia o peso a partir da LISTA em que o cartão caía, e
 * não a partir da hora.
 *
 * ## `hoje` e `amanhã` são palavras que `faltaQuanto` não sabe dizer
 *
 * E não são uma segunda régua de tempo: elas respondem a outra pergunta.
 * `faltaQuanto` mede *quanto falta* («em 20 horas»), e estas dizem *em que dia*
 * — e um encontro às 19:00 de amanhã dito como «em 20 horas» faz a pessoa fazer
 * a conta que a tela já podia ter feito.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ── A CONTAGEM DOS DIAS, E A ORDEM DE 25/09/2026 (a segunda do dia) ─────────
 * ════════════════════════════════════════════════════════════════════════════
 *
 * > *«coloque a quantidade de dias com mais ênfase, usando princípios de
 * > marketing e copywriting para dar mais atenção que o evento não será no dia
 * > atual.»*
 *
 * A tabela acima já dizia que **o dia manda enquanto o dia é a dúvida**; o que
 * faltava era o corpo. `EM 5 DIAS` saía a 11 px, do tamanho de uma legenda de
 * rodapé, a fazer o trabalho mais importante do cartão. Agora a peça separa-se
 * em duas: **o algarismo vai em corpo grande e as palavras ficam pequenas** — e
 * é por isso que `dias` existe neste contrato, ao lado de `palavra`. *Quem
 * desenha precisa do número sozinho para o poder pôr numa escala própria; quem
 * lê em voz alta precisa da frase inteira.* As duas nunca se contradizem, porque
 * a frase é construída a partir do número.
 *
 * ## As quatro palavras, e por que cada uma é aquela
 *
 * A régua de copywriting desta casa tem três leis que mordem aqui: *clareza
 * acima de esperteza*, *específico acima de vago* e **honesto acima de
 * sensacional**. O trabalho desta linha é **um só** — dizer «isto não é hoje».
 *
 * | distância | a frase | por que assim |
 * |---|---|---|
 * | a decorrer / já foi | `acontecendo agora` · `já aconteceu` | de `faltaQuanto`, intactas |
 * | entrada aberta | `em 8 minutos` | de `faltaQuanto`: a essa altura a unidade é o minuto |
 * | hoje | `hoje` | e a hora sai em corpo 26 na placa do cartão |
 * | amanhã | **`é amanhã`** | é como as pessoas falam, e é mais curto e mais claro que «falta 1 dia» |
 * | 2 dias ou mais | **`faltam N dias`** | `faltam` é verbo activo e NOMEIA a distância; «em 5 dias» lê-se como um horário |
 *
 * **`falta 1 dia` não existe, e isso é um teorema, não um cuidado:** um dia de
 * distância é `amanhã` por calendário, e o ramo dos dias só é alcançado depois
 * de `hoje` e de `amanhã` terem sido descartados. Logo `N ≥ 2` **sempre**, e a
 * concordância nunca tem de ser escolhida em tempo de execução. A cerca prova-o
 * varrendo os quatro degraus no mesmo caso.
 *
 * ## O QUE ESTA FRASE NÃO PODE TER — e sai por ordem escrita do dono
 *
 * Ponto de exclamação, «corra», «últimas vagas», contagem em horas para criar
 * aflição, qualquer palavra que invente escassez. *O encontro não tem vaga
 * limitada, e fingir que tem é mentir na tela.* **A ênfase é de legibilidade, e
 * não de pressão** — é o que separa o corpo grande de um cronómetro a piscar.
 *
 * ## A contagem é de CALENDÁRIO, e não de horas a dividir por 24
 *
 * `faltaQuanto` arredonda horas (`Math.round(horas / 24)`), e isso escreve duas
 * coisas falsas na fronteira da meia-noite: às 23h30 de hoje, um encontro à
 * 00h30 de depois de amanhã está a 25 horas e sairia como **«falta 1 dia»** —
 * singular proibido, e um dia a menos do que o calendário diz. Aqui a conta é a
 * diferença entre as duas CHAVES DE DIA, na mesma grelha em que `hoje` e
 * `amanhã` foram decididos. *Três degraus medidos por réguas diferentes
 * discordariam entre si num minuto do dia, e seria sempre o mesmo minuto.*
 *
 * ## **A chamada NUNCA contém uma hora de relógio**, e isso é uma regra
 *
 * A primeira versão desta função devolvia `hoje às 20:29` e `amanhã às 15:29`. O
 * retrato mostrou o preço: o cartão escrevia `HOJE ÀS 20:29` na rubrica e
 * `20:29` em mono 26 doze pixéis abaixo, e o de amanhã repetia a hora que a
 * linha da medida já dava. **Cada facto tem um lugar só nesta tela:**
 *
 * | facto | onde mora |
 * |---|---|
 * | a hora, no peso **pleno** | `hora-da-placa`, em mono 26 — o único número grande da coluna |
 * | a hora, no peso **sereno** | a linha da medida, a 12 px, ao lado da data |
 * | *quando*, em palavra | **aqui**, e só aqui |
 *
 * *Uma peça que repete a peça ao lado não reforça: ela gasta o lugar em que
 * caberia a coisa que ainda não foi dita.*
 */
export type PesoDoEncontro = 'pleno' | 'sereno';

export interface ChamadaDoTempo {
  /** A frase, já pronta e inteira — é ela que um leitor de tela lê. */
  palavra: string;
  /**
   * A distância em DIAS DE CALENDÁRIO, e só quando a frase é `faltam N dias`.
   *
   * `null` em todos os outros degraus — e `null` aqui quer dizer *«esta chamada
   * não conta dias»*, nunca «zero dias». É por este campo que o cartão põe o
   * algarismo numa escala própria sem ter de partir a frase com uma expressão
   * regular, e ele é sempre **≥ 2** (ver o cabeçalho).
   */
  dias: number | null;
  peso: PesoDoEncontro;
  epoca: EstadoDoEvento;
  /** A janela da entrada do núcleo — quem decide entre a caixa e o botão. */
  entradaAberta: boolean;
  /** O instante em que o botão aparece, para a tela poder NOMEÁ-LO. */
  abreEm: Date;
}

/** O que a tela tem em mãos: o DTO da rota, com `startsAt` em texto. */
export interface EncontroNoTempo {
  startsAt: string;
  durationMinutes: number;
}

export function chamadaDoTempo(
  e: EncontroNoTempo,
  agora: Date,
  meuFuso: string,
  /*
   * O tradutor (`A8`). Esta função é PURA e não é um componente: não pode
   * chamar o `useT()`, e por isso o tradutor entra por parâmetro, de quem a
   * chama — que é sempre uma tela. O padrão é o português, que é o dicionário
   * base, e é o que as bancadas vêem.
   */
  t: Tradutor = tradutorDe(null),
): ChamadaDoTempo {
  const inicio = new Date(e.startsAt);
  const evento = { startsAt: inicio, durationMinutes: e.durationMinutes };
  const epoca = estadoDoEvento(evento, agora);
  const entradaAberta = entradaEstaAberta(evento, agora);
  const abreEm = entradaAbertaEm(evento);

  /* O passado e o presente não têm distância: as duas frases saem inteiras de
     `faltaQuanto`, que já as escreve («acontecendo agora», «já aconteceu»). */
  if (epoca !== 'futuro') {
    return {
      palavra: faltaQuanto(evento, agora, t),
      dias: null,
      peso: epoca === 'agora' ? 'pleno' : 'sereno',
      epoca,
      entradaAberta,
      abreEm,
    };
  }

  const hoje: ChaveDeDia = diaDoInstante(agora, meuFuso);
  const noDia: ChaveDeDia = diaDoInstante(inicio, meuFuso);

  /* A entrada aberta manda sobre o calendário: ela é sempre «daqui a minutos»,
     e `faltaQuanto` já a escreve na unidade certa. */
  if (entradaAberta) {
    const palavra = faltaQuanto(evento, agora, t);
    return { palavra, dias: null, peso: 'pleno', epoca, entradaAberta, abreEm };
  }
  if (noDia === hoje) {
    return {
      palavra: t('encontro.hoje'),
      dias: null,
      peso: 'pleno',
      epoca,
      entradaAberta,
      abreEm,
    };
  }
  if (noDia === somarDias(hoje, 1)) {
    return {
      palavra: t('encontro.eAmanha'),
      dias: null,
      peso: 'sereno',
      epoca,
      entradaAberta,
      abreEm,
    };
  }
  /* A diferença entre as duas chaves, na grelha do calendário — e nunca horas a
     dividir por 24. Os dois ramos de cima garantem `dias >= 2`. */
  const dias = Math.round((utcDoDia(noDia) - utcDoDia(hoje)) / UM_DIA);
  return {
    palavra: t('encontro.faltamDias', { dias }),
    dias,
    peso: 'sereno',
    epoca,
    entradaAberta,
    abreEm,
  };
}

/**
 * O peso, sozinho — para quem só precisa dele (a faixa da entrada da área).
 *
 * É a MESMA conta, e não uma cópia dela: a faixa da vitrine e o cartão da agenda
 * são as duas peças que o dono comparou lado a lado («ele merece um outro
 * espaço»), e duas contas de peso seriam duas telas a discordar sobre o mesmo
 * encontro no mesmo minuto.
 */
export function pesoDoEncontro(
  e: EncontroNoTempo,
  agora: Date,
  meuFuso: string,
  t: Tradutor = tradutorDe(null),
): PesoDoEncontro {
  return chamadaDoTempo(e, agora, meuFuso, t).peso;
}
